import type { AnswerValue, Answers, Question } from "../types";

/** A dependsOn-gated question that is currently hidden must not count toward totals. */
export function isQuestionApplicable(question: Question, answers: Answers): boolean {
  if (!question.dependsOn) return true;
  const controlling = answers[question.dependsOn.questionId];
  const expected = question.dependsOn.equals;
  // Multi-select parent (e.g. top_visuals → "sonstige"): contains-semantics,
  // not equality — the controlling value is an array of selected options.
  if (Array.isArray(controlling)) {
    return Array.isArray(expected)
      ? expected.some((entry) => controlling.includes(entry))
      : controlling.includes(expected);
  }
  return Array.isArray(expected) ? expected.includes(String(controlling)) : controlling === expected;
}

export function isAnswered(value: AnswerValue): boolean {
  return !(value === undefined || value === "" || (Array.isArray(value) && value.length === 0));
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Progress is measured over mandatory questions only — optional text fields
 *  (e.g. Berichts-/Projektname) must not keep the bar from reaching 100 %. */
export function countRequiredTotal(sections: { questions: Question[] }[], answers: Answers): number {
  return sections.reduce(
    (sum, section) =>
      sum +
      section.questions.filter(
        (question) => question.required && isQuestionApplicable(question, answers)
      ).length,
    0
  );
}

export function countRequiredAnswered(sections: { questions: Question[] }[], answers: Answers): number {
  let count = 0;
  for (const section of sections) {
    for (const question of section.questions) {
      if (!question.required) continue;
      if (!isQuestionApplicable(question, answers)) continue;
      if (isAnswered(answers[question.id])) count += 1;
    }
  }
  return count;
}

/**
 * Required-field check for gating the send button: every applicable required
 * question answered, and any `format: "email"` field a plausible address (a
 * filled but broken e-mail would make the returned briefing un-answerable).
 */
export function countRequiredMissing(
  sections: { questions: Question[] }[],
  answers: Answers
): number {
  let missing = 0;
  for (const section of sections) {
    for (const question of section.questions) {
      if (!question.required) continue;
      if (!isQuestionApplicable(question, answers)) continue;
      const value = answers[question.id];
      if (!isAnswered(value)) {
        missing += 1;
        continue;
      }
      if (question.format === "email" && typeof value === "string" && !EMAIL_REGEX.test(value.trim())) {
        missing += 1;
      }
    }
  }
  return missing;
}
